import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, MapPin, Edit } from 'lucide-react';
import { toast } from 'sonner';

export default function SiteList() {
  const [sites, setSites] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [address, setAddress] = useState('');
  // Şantiye şefi: ertesi sabah eksik rapor WhatsApp bildirimi ona gider
  const [sef, setSef] = useState({ ad: '', telefon: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchSites(); }, []);

  const fetchSites = async () => {
    try {
      const res = await api.get('/insaat/sites');
      setSites(res.data);
    } catch (err) { console.error(err); }
  };

  const resetForm = () => {
    setEditing(null); setName(''); setCode(''); setAddress(''); setSef({ ad: '', telefon: '' });
  };

  const openNew = () => { resetForm(); setShowForm(true); };
  const openEdit = (site) => {
    setEditing(site); setName(site.name); setCode(site.code || ''); setAddress(site.address || '');
    setSef({ ad: site.santiye_sefi?.ad || '', telefon: site.santiye_sefi?.telefon || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      if (editing) {
        await api.patch(`/insaat/sites/${editing.id}`, { name, code, address, santiye_sefi: sef });
        toast.success('Şantiye güncellendi');
      } else {
        await api.post('/insaat/sites', { name, code, address, santiye_sefi: sef });
        toast.success('Şantiye oluşturuldu');
      }
      setShowForm(false);
      resetForm();
      fetchSites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
    setLoading(false);
  };

  const toggleActive = async (site) => {
    try {
      await api.patch(`/insaat/sites/${site.id}`, { is_active: !site.is_active });
      fetchSites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  return (
    <div data-testid="insaat-site-list-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Şantiyeler</h1>
        <Button onClick={openNew} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-site-btn">
          <Plus size={16} className="mr-2" /> Yeni Şantiye
        </Button>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ad</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Kod</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Adres</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Konumlar</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sites.map((s) => (
              <TableRow key={s.id} className="erp-table-row border-[#d0c4b0]" data-testid={`site-row-${s.id}`}>
                <TableCell className="font-medium text-[#1a1916]">
                  <Link to={`/erp/insaat/sites/${s.id}`} className="flex items-center gap-2 hover:underline"
                    data-testid={`site-link-${s.id}`}>
                    <MapPin size={14} className="text-[#8a7e70]" /> {s.name}
                  </Link>
                </TableCell>
                <TableCell className="text-sm text-[#8a7e70] font-mono">{s.code}</TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{s.address}</TableCell>
                <TableCell className="text-xs text-[#3F3E39]">
                  {(() => {
                    const n = s.konum_sayilari || {};
                    const parca = [n.etap && `${n.etap} etap`, n.blok && `${n.blok} blok / bölge`, n.kat && `${n.kat} kat`, n.bolum && `${n.bolum} bölüm`].filter(Boolean);
                    return parca.length ? parca.join(' · ') : (
                      <Link to={`/erp/insaat/sites/${s.id}`} className="text-[#2e6fa0] hover:underline">Konum tanımla</Link>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <button onClick={() => toggleActive(s)}
                    className={`status-badge ${s.is_active ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]' : 'bg-[#e8dfd0] text-[#8a7e70]'}`}>
                    {s.is_active ? 'Aktif' : 'Pasif'}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(s)} className="h-8 w-8" data-testid={`edit-site-${s.id}`}>
                    <Edit size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {sites.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#8a7e70]">Şantiye bulunamadı</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">{editing ? 'Şantiye Düzenle' : 'Yeni Şantiye'}</DialogTitle>
            <DialogDescription className="text-[#8a7e70] text-sm">Şantiye bilgilerini girin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Şantiye Adı *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white border-[#d0c4b0]" data-testid="site-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Kod</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} className="bg-white border-[#d0c4b0]" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Adres</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} className="bg-white border-[#d0c4b0]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Şantiye şefi</Label>
                <Input value={sef.ad} onChange={(e) => setSef(p => ({ ...p, ad: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" data-testid="site-sef-ad" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Şef telefonu</Label>
                <Input value={sef.telefon} onChange={(e) => setSef(p => ({ ...p, telefon: e.target.value }))}
                  placeholder="0533 …" className="bg-white border-[#d0c4b0]" data-testid="site-sef-telefon" />
              </div>
            </div>
            <p className="text-[11px] text-[#8a7e70] -mt-2">
              Alt yüklenicilerden biri günlük raporu girmezse ertesi sabah şefe WhatsApp ile bildirilir (açıksa).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="save-site-btn">
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
